import { activeLocale, applySavedLocale } from '@/i18n'
import { defineStore } from 'pinia'
import { HubConnectionBuilder, HubConnectionState, LogLevel, type HubConnection } from '@microsoft/signalr'
import { api, json } from '@/services/api'
import type { Account, Activity, ClientBootstrap, GroceryItem, GroceryWorkMode, ListWorkMode, Meal, PackingItem, PersonalPage, PreparationItem, Profile, TripDetail, TripSummary } from '@/types/api'
import { configureAdminRoute } from '@/router'
import { clearNextTimeQueue, useNextTimeStore } from '@/stores/nextTime'

type TripSection = 'packing'|'activities'|'meals'|'groceries'|'personal'|'before-you-go'|'gear-kits'
type RealtimeStatus = 'disconnected'|'connecting'|'connected'|'reconnecting'
type TripChangeMessage = { tripId: string, sections: string[] }

let connection: HubConnection | undefined
let activeTripId: string | undefined
let watchedSection: TripSection | undefined
let reconnectTimer: number | undefined
let refreshTimer: number | undefined
const pendingRefreshes = new Set<string>()

export const useTripStore = defineStore('trips', {
  state: () => ({
    subscription: null as import('@/services/subscriptions').SubscriptionAccount | null,
    trips: [] as TripSummary[], current: null as TripDetail | null,
    gearKits: null as import('@/types/api').TripGearPage | null,
    packing: [] as PackingItem[], activities: [] as Activity[], meals: [] as Meal[],
    groceries: [] as GroceryItem[], personal: null as PersonalPage | null, beforeYouGo: [] as PreparationItem[], account: null as Account | null, profile: null as Profile | null,
    loading: false, hasSession: false, isAdmin: false, adminPath: null as string | null, showTrailMail: false,
    bootstrapped: false, realtimeStatus: 'disconnected' as RealtimeStatus
  }),
  actions: {
    async refreshLanguage() {
      const work: Promise<unknown>[] = []
      if (this.trips.length) work.push(this.loadTrips())
      if (this.current) {
        work.push(this.loadTrip(this.current.id, true))
        if (watchedSection) work.push(this.loadSection(this.current.id, watchedSection))
      }
      await Promise.all(work)
    },
    async loadTrips() { this.trips = await api<TripSummary[]>('/api/trips') },
    async loadTrip(id: string, quiet = false) {
      if (!quiet) this.loading = true
      try { this.current = await api<TripDetail>(`/api/trips/${id}`) }
      finally { this.loading = false }
    },
    async loadSection(id: string, section: TripSection) {
      if (section === 'gear-kits') {
        this.gearKits = await api<import('@/types/api').TripGearPage>(`/api/trips/${id}/gear-kits`)
        this.packing = this.gearKits.packing
        return
      }
      if (section === 'before-you-go') {
        this.beforeYouGo = await api<PreparationItem[]>(`/api/trips/${id}/before-you-go`)
        return
      }
      ;(this as any)[section] = await api(`/api/trips/${id}/${section}`)
    },

    async connectToTrip(id: string) {
      if (activeTripId === id && connection && connection.state !== HubConnectionState.Disconnected) return
      await this.disconnectFromTrip()
      activeTripId = id
      const store = this
      connection = new HubConnectionBuilder()
        .withUrl('/hubs/trips')
        .withAutomaticReconnect([0, 2_000, 5_000, 10_000])
        .configureLogging(LogLevel.Warning)
        .build()

      connection.on('TripChanged', (message: TripChangeMessage) => {
        if (message.tripId.toLowerCase() !== activeTripId?.toLowerCase()) return
        for (const section of message.sections) pendingRefreshes.add(section)
        if (refreshTimer) window.clearTimeout(refreshTimer)
        refreshTimer = window.setTimeout(async () => {
          refreshTimer = undefined
          const tripId = activeTripId
          const sections = new Set(pendingRefreshes)
          pendingRefreshes.clear()
          if (!tripId) return
          const work: Promise<unknown>[] = []
          if (sections.has('trip')) work.push(store.loadTrip(tripId, true))
          if (watchedSection && sections.has(watchedSection)) work.push(store.loadSection(tripId, watchedSection))
          await Promise.all(work).catch(() => undefined)
        }, 75)
      })
      connection.on('AccessRevoked', async (message: {message:string,code?:string}) => {
        store.realtimeStatus = 'disconnected'
        store.current = null
        await oldSafeStop()
        const code = message.code === 'session_signed_out' ? 'session_signed_out' : 'trip_access_changed'
        window.location.assign(`/trips?noticeCode=${code}&lang=${activeLocale.value}`)
      })
      connection.onreconnecting(() => { store.realtimeStatus = 'reconnecting' })
      connection.onreconnected(async () => {
        if (!activeTripId || !connection) return
        store.realtimeStatus = 'connected'
        await connection.invoke('JoinTrip', activeTripId)
        await Promise.all([
          store.loadTrip(activeTripId, true),
          ...(watchedSection ? [store.loadSection(activeTripId, watchedSection)] : [])
        ]).catch(() => undefined)
      })
      connection.onclose(() => {
        store.realtimeStatus = 'disconnected'
        if (activeTripId) store.scheduleRealtimeRetry(activeTripId)
      })
      await this.startRealtime(id)
      async function oldSafeStop() { try { await connection?.stop() } catch { /* already disconnected */ } }
    },
    async startRealtime(id: string) {
      if (!connection || activeTripId !== id || connection.state !== HubConnectionState.Disconnected) return
      this.realtimeStatus = 'connecting'
      try {
        await connection.start()
        if (activeTripId !== id) return
        await connection.invoke('JoinTrip', id)
        this.realtimeStatus = 'connected'
      } catch {
        this.realtimeStatus = 'disconnected'
        this.scheduleRealtimeRetry(id)
      }
    },
    scheduleRealtimeRetry(id: string) {
      if (reconnectTimer) window.clearTimeout(reconnectTimer)
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = undefined
        if (activeTripId === id) this.startRealtime(id)
      }, 5_000)
    },
    watchSection(section?: TripSection) { watchedSection = section },
    clearWatchedSection() { watchedSection = undefined },
    async disconnectFromTrip() {
      activeTripId = undefined
      watchedSection = undefined
      pendingRefreshes.clear()
      if (reconnectTimer) window.clearTimeout(reconnectTimer)
      if (refreshTimer) window.clearTimeout(refreshTimer)
      reconnectTimer = refreshTimer = undefined
      const oldConnection = connection
      connection = undefined
      this.realtimeStatus = 'disconnected'
      if (oldConnection && oldConnection.state !== HubConnectionState.Disconnected) await oldConnection.stop()
    },

    async loadBootstrap() {
      const result = await api<ClientBootstrap>('/api/bootstrap')
      applySavedLocale(result.preferredLocale)
      this.hasSession = result.hasSession
      this.account = result.account
      this.profile = result.profile
      this.isAdmin = result.isAdmin
      this.adminPath = result.isAdmin ? result.adminPath || null : null
      this.showTrailMail = result.showTrailMail
      configureAdminRoute(this.adminPath)
      this.bootstrapped = true
      return result
    },
    async loadAccount() { const result = await api<{account: Account | null}>('/api/account/me'); this.account = result.account },
    async loadProfile() { this.profile = await api<Profile>('/api/profile'); this.hasSession = true },
    async logout() { await api('/api/account/logout', { method: 'POST' }); this.account = null; useNextTimeStore().clear(); await clearNextTimeQueue().catch(() => undefined); this.isAdmin = false; this.adminPath = null; this.showTrailMail = false; configureAdminRoute(null); await Promise.all([this.loadTrips(),this.loadProfile()]) },
    async removeMember(memberId: string) { if (!this.current) return; await api(`/api/trips/${this.current.id}/members/${memberId}`, { method: 'DELETE' }); await this.loadTrip(this.current.id) },
    async setListMode(mode: ListWorkMode) {
      if (!this.current) return
      const prior = this.current.listMode
      this.current.listMode = mode
      try {
        const result = await api<{mode: ListWorkMode}>(`/api/trips/${this.current.id}/members/me/list-mode`, json('PATCH', { mode }))
        if (this.current) this.current.listMode = result.mode
      } catch (error) {
        if (this.current) this.current.listMode = prior
        throw error
      }
    },
    async setGroceryMode(mode: GroceryWorkMode) {
      if (!this.current) return
      const prior = this.current.groceryMode
      this.current.groceryMode = mode
      try {
        const result = await api<{mode: GroceryWorkMode}>(`/api/trips/${this.current.id}/members/me/grocery-mode`, json('PATCH', { mode }))
        if (this.current) this.current.groceryMode = result.mode
      } catch (error) {
        if (this.current) this.current.groceryMode = prior
        throw error
      }
    },
    async patch(path: string, body: unknown) { await api(path, json('PATCH', body)) }
  }
})
